import { type RefObject, useLayoutEffect, useRef, useState } from 'react'

/*
 * Measures actual rendered column widths from an AntD Table's <colgroup>
 * via a ResizeObserver. Returns the measured widths so that adjacent
 * DOM elements (e.g. expanded-row grids) can share the exact same column
 * edges as the parent table rows, regardless of AntD sizing internals.
 *
 * Falls back to the provided `fallback` array until the first measurement
 * succeeds (or if the container ref is not yet attached).
 */
const useTableColumnWidths = (
  fallback: readonly number[]
): {
  containerRef: RefObject<HTMLDivElement>
  columnWidths: readonly number[]
} => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [columnWidths, setColumnWidths] = useState<readonly number[]>(fallback)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const measure = () => {
      // AntD may render separate colgroups for the header table and body
      // table. The body colgroup is the source of truth for td widths.
      const bodyColgroup =
        container.querySelector('.ant-table-body colgroup') ??
        container.querySelector('.ant-table-content colgroup') ??
        container.querySelector('colgroup')
      if (!bodyColgroup) return

      const cols = Array.from(
        bodyColgroup.querySelectorAll('col')
      ) as HTMLTableColElement[]
      if (cols.length === 0) return

      const widths = cols.map((col) => {
        const rect = col.getBoundingClientRect()
        if (rect.width > 0) return Math.round(rect.width)
        // <col> elements often have 0 layout size; fall back to parsing
        // the inline style width AntD sets on them.
        const parsed = parseFloat(col.style.width || '0')
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
      })

      if (widths.some((w) => w <= 0)) return

      setColumnWidths((prev) => {
        if (
          prev.length === widths.length &&
          prev.every((w, i) => w === widths[i])
        ) {
          return prev
        }
        return widths
      })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return { containerRef, columnWidths }
}

export { useTableColumnWidths }
